<template>
  <div class="locale" @keydown.escape="open = false">
    <button
      type="button"
      class="locale__trigger"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-label="t('nav.language')"
      @click="open = !open"
    >
      <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.4" />
        <path d="M2.5 10h15M10 2.5c2 2.4 2 12.6 0 15M10 2.5c-2 2.4-2 12.6 0 15"
              stroke="currentColor" stroke-width="1.4" />
      </svg>
      <span>{{ current.native }}</span>
    </button>

    <ul v-if="open" class="locale__menu" role="listbox">
      <li v-for="entry in locales" :key="entry.code">
        <button
          type="button"
          role="option"
          :aria-selected="entry.code === locale"
          :class="['locale__option', { 'is-active': entry.code === locale }]"
          @click="choose(entry.code)"
        >
          {{ entry.native }}
          <svg v-if="entry.code === locale" width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
            <path d="m4 10.5 4 4 8-9" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { LOCALES, setLocale } from "../i18n";

/**
 * The language switcher.
 *
 * Languages are listed in their OWN language — a Russian speaker looking for
 * their language scans for "Русский", not for "Russian". Listing them in the
 * current UI language makes the menu useless to exactly the person who needs it.
 */

const { t, locale } = useI18n();
const locales = LOCALES;
const open = ref(false);

const current = computed(
  () => locales.find((entry) => entry.code === locale.value) || locales[0]
);

function choose(code) {
  setLocale(code);
  open.value = false;
}

// Close on any click outside. Registered on mousedown rather than click so the
// menu is gone before the underlying control reacts.
function onDocument(event) {
  if (!event.target.closest?.(".locale")) open.value = false;
}
onMounted(() => document.addEventListener("mousedown", onDocument));
onBeforeUnmount(() => document.removeEventListener("mousedown", onDocument));
</script>

<style scoped>
.locale { position: relative; }

.locale__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 34px;
  padding: 0 var(--space-3);
  border: 1px solid transparent;
  border-radius: var(--radius);
  background: transparent;
  color: var(--ink-muted);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: background var(--transition), color var(--transition);
}
.locale__trigger:hover { background: var(--bg-sunken); color: var(--ink-strong); }

.locale__menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 168px;
  margin: 0;
  padding: var(--space-1);
  list-style: none;
  background: var(--bg-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.locale__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
}
.locale__option:hover { background: var(--bg-sunken); }
.locale__option.is-active { color: var(--accent); font-weight: 500; }
</style>
